// src/components/SocialTab.jsx
// Social hub — Feed + Notifications + Friends (with people search)
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
import FeedTab from "./FeedTab";
import NotificationTab from "./NotificationTab";
import FriendsTab from "./FriendsTab";

const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54",
  primaryDim: "#595149",
  bg: "#faf9f6",
  surfaceLow: "#f4f4f0",
  container: "#edeeea",
  surfaceLowest: "#ffffff",
  onSurface: "#2f3430",
  outlineVariant: "#8a8e8a",
};

export default function SocialTab({
  personalPlaces,
  onPlaceClick,
  onDataChange,
  onNavigate,
  onUnreadChange,
  onNotificationPlaceClick,
  onViewUserProfile,
  onViewTrip,
}) {
  const { user } = useUser();
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState("feed"); // "feed" | "notifications" | "friends"

  // Badge counts
  const [unreadCount, setUnreadCount] = useState(0);
  const [requestCount, setRequestCount] = useState(0);

  // Fetch unread notification count
  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/notifications/`)
      .then((res) => {
        const count = res.data.filter((n) => !n.is_read).length;
        setUnreadCount(count);
      })
      .catch(() => {});
  }, [user]);

  // Fetch follow request count
  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/follows/me/requests`)
      .then((res) => setRequestCount(res.data.length))
      .catch(() => {});
  }, [user]);

  const handleUnreadChange = useCallback((count) => {
    setUnreadCount(count);
    if (onUnreadChange) onUnreadChange(count);
  }, [onUnreadChange]);

  const handleRequestCountChange = useCallback((count) => {
    setRequestCount(count);
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {/* Segment toggle */}
      <div style={{
        padding: "14px 16px 0",
        background: C.bg,
        position: "sticky", top: 0, zIndex: 5,
      }}>
        <div style={{
          display: "flex", gap: 0, width: "100%",
          background: "rgba(237,238,234,0.6)",
          borderRadius: 10, padding: 3,
        }}>
          {[
            { key: "feed", label: t("tabs.feed"), badge: 0 },
            { key: "notifications", label: t("tabs.notifications"), badge: unreadCount },
            { key: "friends", label: t("tabs.friends"), badge: requestCount },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSubTab(tab.key)}
              style={{
                flex: 1, padding: "8px 0",
                background: subTab === tab.key ? "#ffffff" : "transparent",
                border: "none", borderRadius: 8,
                fontFamily: FL, fontSize: 13, fontWeight: subTab === tab.key ? 700 : 500,
                color: subTab === tab.key ? C.primary : C.outlineVariant,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: subTab === tab.key ? "0 1px 4px rgba(47,52,48,0.08)" : "none",
                position: "relative",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 16, height: 16, borderRadius: 8,
                  background: "#d4537e", color: "#fff",
                  fontFamily: FL, fontSize: 10, fontWeight: 700,
                  padding: "0 4px",
                  lineHeight: 1,
                }}>
                  {tab.badge > 99 ? "99+" : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {subTab === "feed" ? (
          <FeedTab
            personalPlaces={personalPlaces}
            onPlaceClick={onPlaceClick}
            onDataChange={onDataChange}
            onNavigate={onNavigate}
            onSwitchToFriends={() => setSubTab("friends")}
            onViewTrip={onViewTrip}
          />
        ) : subTab === "notifications" ? (
          <NotificationTab
            onUnreadChange={handleUnreadChange}
            onPlaceClick={onNotificationPlaceClick}
          />
        ) : (
          <FriendsTab
            onViewUserProfile={onViewUserProfile}
            onRequestCountChange={handleRequestCountChange}
          />
        )}
      </div>
    </div>
  );
}
